<template>
  <div class="task-graph-panel" v-if="graphData">
    <div class="task-graph-header">
      <div class="header-title">
        <span class="root-id">{{ graphData.rootTaskId }}</span>
        <span class="status-badge" :class="graphData.status.toLowerCase()">{{ graphData.status }}</span>
        <span class="revision-badge">Revision {{ graphData.revision }}</span>
      </div>
      <div class="header-close" @click="$emit('close')">✕</div>
    </div>
    
    <div class="task-graph-body">
      <div class="dag-container" ref="dagContainer">
        <!-- SVG for Edges -->
        <svg class="dag-edges">
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="rgba(0, 242, 248, 0.6)" />
            </marker>
            <marker id="arrowhead-revision" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="rgba(255, 170, 0, 0.6)" />
            </marker>
          </defs>
          <g v-for="(edge, index) in layoutEdges" :key="`edge-${index}`">
            <path 
              :d="edge.path" 
              :stroke="edge.type === 'revision' ? 'rgba(255, 170, 0, 0.6)' : 'rgba(0, 242, 248, 0.6)'" 
              :stroke-width="2"
              fill="none"
              :stroke-dasharray="edge.type === 'revision' ? '4 4' : 'none'"
              :marker-end="edge.type === 'revision' ? 'url(#arrowhead-revision)' : 'url(#arrowhead)'"
            />
            <text v-if="edge.label" :x="edge.midX" :y="edge.midY - 8" fill="rgba(255,170,0,0.8)" font-size="12" text-anchor="middle">
              {{ edge.label }}
            </text>
          </g>
        </svg>

        <!-- HTML Nodes -->
        <div 
          v-for="node in layoutNodes" 
          :key="node.id"
          class="dag-node"
          :class="[node.status.toLowerCase(), { selected: selectedNode && selectedNode.id === node.id }]"
          :style="{ left: node.x + 'px', top: node.y + 'px' }"
          @click="selectNode(node)"
        >
          <div class="node-icon">
            {{ getIconForType(node.type) }}
          </div>
          <div class="node-content">
            <div class="node-title">{{ node.title }}</div>
            <div class="node-meta">
              <span class="node-type">{{ formatType(node.type) }}</span>
              <span class="node-time" v-if="node.startedAt">{{ getDuration(node) }}ms</span>
            </div>
          </div>
          <div class="node-status-indicator"></div>
        </div>
      </div>

      <!-- Detail Panel -->
      <div class="detail-sidebar" v-if="selectedNode">
        <div class="detail-header">
          <h3>节点详情</h3>
          <div class="detail-close" @click="selectedNode = null">✕</div>
        </div>
        <div class="detail-content">
          <div class="info-group">
            <h4>基本信息</h4>
            <div class="info-row"><span>ID:</span> <code>{{ selectedNode.id }}</code></div>
            <div class="info-row"><span>状态:</span> <span :class="['status-text', selectedNode.status.toLowerCase()]">{{ selectedNode.status }}</span></div>
            <div class="info-row"><span>修订版本:</span> <span>{{ selectedNode.revision }}</span></div>
            <div class="info-row"><span>耗时:</span> <span>{{ getDuration(selectedNode) }} ms</span></div>
          </div>

          <div class="info-group" v-if="selectedNode.input">
            <h4>输入参数</h4>
            <pre class="json-viewer">{{ formatJson(selectedNode.input) }}</pre>
          </div>

          <div class="info-group" v-if="selectedNode.output">
            <h4>输出结果</h4>
            <pre class="json-viewer">{{ formatJson(selectedNode.output) }}</pre>
          </div>

          <div class="info-group" v-if="selectedNode.error">
            <h4>错误信息</h4>
            <div class="error-box">{{ selectedNode.error }}</div>
          </div>

          <div class="info-group" v-if="getNodeEvidence(selectedNode).length > 0">
            <h4>执行证据 (Evidence)</h4>
            <div class="evidence-item" v-for="ev in getNodeEvidence(selectedNode)" :key="ev.id">
              <div class="ev-header">
                <span class="ev-source">{{ ev.source }}</span>
                <span class="ev-time">{{ formatTime(ev.timestamp) }}</span>
              </div>
              <pre class="json-viewer">{{ formatJson(ev.payload) }}</pre>
            </div>
          </div>
          
          <div class="action-buttons" v-if="selectedNode.type === 'ACTION_EXECUTION' || selectedNode.type === 'CONTEXT_BINDING'">
             <button class="link-btn" @click="linkToScene(selectedNode)">在场景中定位/查看</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaskGraphPanel',
  props: {
    graphData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedNode: null,
      layoutNodes: [],
      layoutEdges: []
    }
  },
  watch: {
    graphData: {
      handler(newVal) {
        if (newVal) {
          this.calculateLayout()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    getIconForType(type) {
      const map = {
        'USER_INPUT': '🎤',
        'INTENT_RESOLUTION': '🧠',
        'CONTEXT_BINDING': '📍',
        'CONFIRMATION': '🛡️',
        'ACTION_EXECUTION': '⚙️',
        'STATUS_FEEDBACK': '✅',
        'UNDO_ROLLBACK': '↩️'
      }
      return map[type] || '📄'
    },
    formatType(type) {
      return type.replace('_', ' ')
    },
    getDuration(node) {
      if (!node.startedAt || !node.finishedAt) return '-'
      return Math.round((node.finishedAt - node.startedAt) * 1000)
    },
    formatTime(ts) {
      if (!ts) return ''
      return new Date(ts * 1000).toISOString().substr(11, 12)
    },
    formatJson(obj) {
      return JSON.stringify(obj, null, 2)
    },
    getNodeEvidence(node) {
      if (!this.graphData || !this.graphData.evidence || !node.evidenceRefs) return []
      return this.graphData.evidence.filter(ev => node.evidenceRefs.includes(ev.id))
    },
    selectNode(node) {
      this.selectedNode = node
    },
    linkToScene(node) {
      if (node.type === 'CONTEXT_BINDING' && node.input?.resolvedTarget?.objectId) {
         if (window.viewer && window.viewer.entities) {
             const entity = window.viewer.entities.getById(node.input.resolvedTarget.objectId)
             if (entity) window.viewer.selectedEntity = entity
         }
      } else if (node.type === 'ACTION_EXECUTION') {
         const actionName = node.input?.actionName
         if (actionName === 'cesium.selectObject' && node.input?.arguments?.objectId) {
            if (window.viewer && window.viewer.entities) {
               const entity = window.viewer.entities.getById(node.input.arguments.objectId)
               if (entity) window.viewer.selectedEntity = entity
            }
         } else if (actionName === 'flood.showRange') {
            this.$root.$emit('simulate-flood', node.input?.arguments)
         }
      }
    },
    calculateLayout() {
      if (!this.graphData || !this.graphData.nodes) return

      const nodes = this.graphData.nodes
      const edges = this.graphData.edges
      
      // Group by revision
      const revs = {}
      nodes.forEach(n => {
        if (!revs[n.revision]) revs[n.revision] = []
        revs[n.revision].push(n)
      })

      const NODE_WIDTH = 220
      const NODE_HEIGHT = 70
      const X_SPACING = 280
      const Y_SPACING = 120

      const layoutNodesMap = {}
      this.layoutNodes = []
      
      let currentY = 40

      // Layout per revision track
      Object.keys(revs).sort().forEach(revStr => {
        const revNodes = revs[revStr].sort((a, b) => a.startedAt - b.startedAt)
        let currentX = 40
        revNodes.forEach(node => {
          const lNode = {
            ...node,
            x: currentX,
            y: currentY,
            width: NODE_WIDTH,
            height: NODE_HEIGHT
          }
          this.layoutNodes.push(lNode)
          layoutNodesMap[node.id] = lNode
          currentX += X_SPACING
        })
        currentY += Y_SPACING
      })

      // Calculate edge paths
      this.layoutEdges = edges.map(edge => {
        const source = layoutNodesMap[edge.source]
        const target = layoutNodesMap[edge.target]
        if (!source || !target) return null

        const startX = source.x + source.width
        const startY = source.y + source.height / 2
        const endX = target.x
        const endY = target.y + target.height / 2
        
        let path = ''
        if (source.y === target.y) {
           path = `M ${startX} ${startY} L ${endX} ${endY}`
        } else {
           // Cross revision or custom routing
           const midX1 = startX + 20
           const midY1 = startY
           const midX2 = startX + 20
           const midY2 = endY
           path = `M ${startX} ${startY} C ${midX1} ${midY1}, ${midX2} ${midY2}, ${endX} ${endY}`
        }

        return {
          ...edge,
          path,
          midX: (startX + endX) / 2,
          midY: (startY + endY) / 2
        }
      }).filter(Boolean)

      // Update selected node reference if needed
      if (this.selectedNode) {
        this.selectedNode = this.layoutNodes.find(n => n.id === this.selectedNode.id) || null
      }
    }
  }
}
</script>

<style scoped>
.task-graph-panel {
  position: absolute;
  top: 80px;
  left: 20px;
  right: 20px;
  bottom: 120px;
  background: rgba(15, 20, 30, 0.95);
  border: 1px solid rgba(0, 242, 248, 0.3);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 10000;
  backdrop-filter: blur(10px);
  color: #fff;
  font-family: 'Microsoft YaHei', sans-serif;
  overflow: hidden;
}

.task-graph-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid rgba(0, 242, 248, 0.2);
  background: linear-gradient(90deg, rgba(0, 242, 248, 0.1) 0%, transparent 100%);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.root-id {
  font-size: 16px;
  font-weight: bold;
  color: #00f2f8;
}

.status-badge, .revision-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.completed { background: rgba(0, 255, 128, 0.2); color: #00ff80; }
.status-badge.running { background: rgba(0, 162, 255, 0.2); color: #00a2ff; }
.status-badge.cancelled { background: rgba(255, 170, 0, 0.2); color: #ffaa00; }
.status-badge.error { background: rgba(255, 68, 68, 0.2); color: #ff4444; }
.revision-badge { background: rgba(255, 255, 255, 0.1); color: #ccc; }

.header-close {
  cursor: pointer;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.6);
}
.header-close:hover { color: #fff; }

.task-graph-body {
  flex: 1;
  display: flex;
  position: relative;
  overflow: hidden;
}

.dag-container {
  flex: 1;
  position: relative;
  overflow: auto;
  background: radial-gradient(circle at center, rgba(0, 242, 248, 0.05) 0%, transparent 70%);
}

.dag-edges {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  min-width: 2000px;
  min-height: 1000px;
}

.dag-node {
  position: absolute;
  background: rgba(30, 40, 55, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
}

.dag-node:hover, .dag-node.selected {
  border-color: #00f2f8;
  box-shadow: 0 0 16px rgba(0, 242, 248, 0.3);
  transform: translateY(-2px);
}

.node-icon {
  font-size: 24px;
}

.node-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.node-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

.node-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.node-status-indicator {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #1e2837;
}

.dag-node.completed .node-status-indicator { background: #00ff80; }
.dag-node.running .node-status-indicator { background: #00a2ff; box-shadow: 0 0 8px #00a2ff; }
.dag-node.cancelled .node-status-indicator { background: #ffaa00; }
.dag-node.error .node-status-indicator { background: #ff4444; }

.detail-sidebar {
  width: 400px;
  background: rgba(20, 25, 35, 0.95);
  border-left: 1px solid rgba(0, 242, 248, 0.2);
  display: flex;
  flex-direction: column;
}

.detail-header {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-header h3 { margin: 0; font-size: 16px; color: #00f2f8; }
.detail-close { cursor: pointer; color: rgba(255, 255, 255, 0.5); }
.detail-close:hover { color: #fff; }

.detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.info-group {
  margin-bottom: 24px;
}

.info-group h4 {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  border-left: 3px solid #00f2f8;
  padding-left: 8px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
}

.info-row span:first-child {
  width: 80px;
  color: rgba(255, 255, 255, 0.5);
}

.json-viewer {
  background: rgba(0, 0, 0, 0.3);
  padding: 12px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  color: #a5d6ff;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
}

.error-box {
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  padding: 12px;
  border-radius: 6px;
  color: #ff4444;
  font-size: 13px;
}

.status-text.completed { color: #00ff80; }
.status-text.running { color: #00a2ff; }
.status-text.cancelled { color: #ffaa00; }
.status-text.error { color: #ff4444; }

.evidence-item {
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 8px;
}

.ev-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
}

.ev-source {
  color: #ffaa00;
  font-weight: bold;
}

.ev-time {
  color: rgba(255, 255, 255, 0.4);
}

.action-buttons {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.link-btn {
  background: rgba(0, 242, 248, 0.1);
  border: 1px solid #00f2f8;
  color: #00f2f8;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.link-btn:hover {
  background: rgba(0, 242, 248, 0.2);
  box-shadow: 0 0 12px rgba(0, 242, 248, 0.4);
}

/* Custom Scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.1); }
::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
</style>
